"use client";

import {
  Children,
  createContext,
  isValidElement,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";
import type { CandidateProfile } from "@/lib/api/types";
import {
  readCertifications,
  readEducation,
  readExperiences,
  readLanguages,
} from "@/features/candidate/lib/profile-section-data";
import { ProfileContext, type ProfileContextValue } from "./context";
import { ProfileSidebarColumn } from "./layout/profile-sidebar-column";

interface SlotProps {
  children: ReactNode;
}

function Header({ children }: SlotProps) {
  return <>{children}</>;
}
function Tabs({ children }: SlotProps) {
  return <>{children}</>;
}
function Content({ children }: SlotProps) {
  return <>{children}</>;
}
function Sidebar({ children }: SlotProps) {
  return <>{children}</>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findSlot(children: ReactNode, slot: (props: any) => ReactNode): ReactElement | null {
  let found: ReactElement | null = null;
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === slot) {
      found = child as ReactElement;
    }
  });
  return found;
}

/**
 * Carries the mobile-copy of a shared sidebar from `ProfileLayout` down to
 * any `ProfileScrollArea` nested inside its Content slot. When `ProfileLayout`
 * receives a `sidebar` prop it puts the JSX here; `ProfileScrollArea`
 * appends a `md:hidden` copy at the bottom of its viewport so consumers
 * write the sidebar children only once.
 */
const ProfileLayoutMobileSidebarContext = createContext<ReactNode>(null);

export function useProfileLayoutMobileSidebar(): ReactNode {
  return useContext(ProfileLayoutMobileSidebarContext);
}

export interface ProfileLayoutProps {
  profile: CandidateProfile | null;
  profileId: string | null;
  /**
   * Shared sidebar content. When provided, the layout renders this JSX in
   * both positions internally: as the desktop right column AND (via
   * `ProfileScrollArea`'s context bridge) inline at the bottom of the mobile
   * scroll viewport. Use this when the mobile and desktop sidebar contents
   * are identical (search profile sheet). For divergent content (pipeline:
   * desktop has action buttons that mobile does not), omit this prop and use
   * the `<ProfileLayout.Sidebar>` slot + explicit `<Profile.MobileSidebar>`.
   */
  sidebar?: ReactNode;
  children: ReactNode;
}

export function ProfileLayout({ profile, profileId, sidebar, children }: ProfileLayoutProps) {
  const header = findSlot(children, Header);
  const tabs = findSlot(children, Tabs);
  const content = findSlot(children, Content);
  const sidebarSlot = findSlot(children, Sidebar);

  const value = useMemo<ProfileContextValue>(() => {
    const sections = {
      education: readEducation(profile),
      certifications: readCertifications(profile),
      languages: readLanguages(profile),
      experiences: readExperiences(profile),
    };
    const derived = {
      firstName:
        profile?.first_name ?? profile?.full_name?.split(" ")[0] ?? "this person",
    };
    return { profile, profileId, sections, derived };
  }, [profile, profileId]);

  const desktopSidebar = sidebarSlot ?? (sidebar ? (
    <ProfileSidebarColumn>{sidebar}</ProfileSidebarColumn>
  ) : null);

  return (
    <ProfileContext.Provider value={value}>
      <ProfileLayoutMobileSidebarContext.Provider value={sidebar ?? null}>
        <div className="flex min-h-0 flex-1 flex-col">
          {header}
          {tabs}
          <div className="flex min-h-0 flex-1 flex-col md:flex-row">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">{content}</div>
            {desktopSidebar}
          </div>
        </div>
      </ProfileLayoutMobileSidebarContext.Provider>
    </ProfileContext.Provider>
  );
}

ProfileLayout.Header = Header;
ProfileLayout.Tabs = Tabs;
ProfileLayout.Content = Content;
ProfileLayout.Sidebar = Sidebar;
